import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
    Box, Button, Checkbox, Chip, Divider, FormControl, FormHelperText, IconButton,
    InputAdornment, InputLabel, ListItemIcon, Menu, MenuItem, OutlinedInput, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
    Toolbar, Tooltip, Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { v4 } from "uuid";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMoeda } from "../../../core/hooks/useMoeda";
import { useCadastrarEditarProdutoContext } from "../../contexts/CadastrarEditarProdutoContext";
import { IAdicionalPizza } from "../../interfaces/IAdicionalPizza";
import { IImagemProduto } from "../../interfaces/IImagemProduto";
import { MIMETypes } from "../../../core/enum/MIMETypes";
import { useSnackbar } from "../../../core/contexts/SnackbarContext";
import { adicionalPizzaSchema } from "../../schema/adicionalPizzaSchema";
import { CardUploadImagem } from "../../../core/components/CardUploadImagem";
import { ImagemContainer } from "../ConfiguracaoPizzaContainer/styles";
import { ArrowDownward, ArrowDropDown, ArrowUpward, PowerSettingsNew } from '@mui/icons-material';
import { useProdutos } from '../../hooks/useProdutos';
import { ConfirmacaoAcaoModal } from '../../../core/components/ConfirmacaoAcaoModal';
import { IProduto } from '../../interfaces/IProduto';
import { MenuOrdenacao } from '../../components/MenuOrdenacao';
import styled from "@emotion/styled";

const Container = styled(Paper)`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
    padding: 1rem;
    flex-direction: column;
`

const FiltroContainer = styled(Box)`
    width: 70%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    button { margin-left: 0.5rem; }
`

const TabelaVazia = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 160px;
    height: 100%;
`

const ButtonsContainerForm = styled(Box)`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 1rem;
    button:first-child { margin-right: 0.5rem; }
`

const ButtonsContainer = styled(Box)`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 2rem;
    button:first-child { margin-right: 0.5rem; }
`

type Tabs = "tabela" | "formulario";

export const ConfiguracaoAdicionaisContainer = () => {
    const { adicionais, adicionarAdicional, editarAdicional, excluirAdicional, ordenarAdicionais, trocarPosicaoAdicional, handleVoltar, handleProximo, produto, categoriaUuid } = useCadastrarEditarProdutoContext();
    const [tab, setTab] = useState<Tabs>("tabela");
    const [adicionalEdicao, setAdicionalEdicao] = useState<IAdicionalPizza | undefined>(undefined);
    const [adicionaisSelecionados, setAdicionaisSelecionados] = useState<string[]>([]);
    const [confirmacaoExclusao, setConfirmacaoExclusao] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [imagem, setImagem] = useState<File | IImagemProduto | undefined>(undefined);
    const { real, number } = useMoeda();
    const snackbar = useSnackbar();
    const { salvar } = useProdutos();

    const { reset, watch, setValue, register, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(adicionalPizzaSchema),
        mode: "onChange",
    });

    const descricao = watch("descricao");
    const preco = watch("preco");
    const detalhes = watch("detalhes");

    const mimetypesValidos = [
        MIMETypes["image/jpeg"],
        MIMETypes["image/jpg"],
        MIMETypes["image/png"]
    ] as string[];

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const limparAdicionaisSelecionados = () => setAdicionaisSelecionados([]);

    const toggleSelecionado = (uuid: string) => {
        setAdicionaisSelecionados(prev =>
            prev.includes(uuid) ? prev.filter(id => id !== uuid) : [...prev, uuid]
        );
    };

    const toggleSelecionarTodos = () => {
        if (adicionaisSelecionados.length === adicionais.length) {
            limparAdicionaisSelecionados();
        } else {
            setAdicionaisSelecionados(adicionais.map(a => a.uuid));
        }
    };

    const handleTab = (tab: Tabs) => {
        setTab(tab);
        setAdicionalEdicao(undefined);
        limparAdicionaisSelecionados();
    };

    const handleUploadImagem = (arquivo: File) => {
        if (!mimetypesValidos.includes(arquivo.type)) {
            return snackbar(`Arquivo inválido, tipos aceitos: (PNG, JPEG)`, { severity: "error" });
        }
        setImagem(arquivo);
    };

    const handleExcluirImagem = () => setImagem(undefined);

    const handleAbrirEdicao = () => {
        const adicional = adicionais.find(a => a.uuid === adicionaisSelecionados[0]);
        if (!adicional) return;
        setTab("formulario");
        setAdicionalEdicao(adicional);
        handleClose();
    };

    const handleAbrirExclusao = () => {
        setConfirmacaoExclusao(true);
        handleClose();
    };

    const handleFecharExclusao = () => setConfirmacaoExclusao(false);

    const handleExcluirAdicionais = () => {
        adicionaisSelecionados.forEach(uuid => excluirAdicional(uuid));
        handleFecharExclusao();
        limparAdicionaisSelecionados();
    };

    const handleToggleAtivacao = async () => {
        if (!produto || !categoriaUuid || adicionaisSelecionados.length !== 1) return;
        const adicional = adicionais.find(a => a.uuid === adicionaisSelecionados[0]);
        if (!adicional) return;
        const adicionalAtualizado: IAdicionalPizza = { ...adicional, inativadoAs: adicional.inativadoAs ? undefined : new Date() };
        editarAdicional(adicionalAtualizado);
        await salvar(categoriaUuid, {
            ...produto,
            adicionais: adicionais.map(a => a.uuid === adicional.uuid ? adicionalAtualizado : a)
        } as IProduto);
        handleClose();
    };

    const adicionalSelecionado = adicionaisSelecionados.length === 1
        ? adicionais.find(a => a.uuid === adicionaisSelecionados[0])
        : undefined;

    const handleCadastrar = (values: FieldValues) => {
        const imagens: IImagemProduto[] = imagem ? [imagem as IImagemProduto] : [];
        adicionarAdicional({
            uuid: v4(),
            descricao: values.descricao,
            preco: number(values.preco),
            detalhes: values.detalhes || undefined,
            imagens,
        });
        handleTab("tabela");
        reset();
        setImagem(undefined);
    };

    const handleEditar = (values: FieldValues) => {
        if (!adicionalEdicao) return;
        const imagens: IImagemProduto[] = imagem ? [imagem as IImagemProduto] : [];
        editarAdicional({
            uuid: adicionalEdicao.uuid,
            descricao: values.descricao,
            preco: number(values.preco),
            detalhes: values.detalhes || undefined,
            imagens,
            inativadoAs: adicionalEdicao.inativadoAs,
        });
        handleTab("tabela");
    };

    useEffect(() => {
        if (adicionalEdicao) {
            setValue("descricao", adicionalEdicao.descricao);
            setValue("preco", adicionalEdicao.preco);
            setValue("detalhes", adicionalEdicao.detalhes ?? "");
            setImagem(adicionalEdicao.imagens?.length ? adicionalEdicao.imagens[0] : undefined);
        } else {
            reset();
            setImagem(undefined);
        }
    }, [adicionalEdicao]);

    /** Posição no array que vai ser salvo, que é a ordem de exibição. */
    const indiceOrdem = (uuid: string) => adicionais.findIndex(a => a.uuid === uuid);

    const renderizarLinha = (adicional: IAdicionalPizza) => (
        <TableRow key={adicional.uuid} sx={{ opacity: adicional.inativadoAs ? 0.5 : 1 }}>
            <TableCell align="center">
                <Checkbox
                    checked={adicionaisSelecionados.includes(adicional.uuid)}
                    onChange={() => toggleSelecionado(adicional.uuid)}
                />
            </TableCell>
            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                <Tooltip title="Mover para cima">
                    <span>
                        <IconButton
                            size="small"
                            disabled={indiceOrdem(adicional.uuid) <= 0}
                            onClick={() => trocarPosicaoAdicional(adicional.uuid, "cima")}
                        >
                            <ArrowUpward fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Mover para baixo">
                    <span>
                        <IconButton
                            size="small"
                            disabled={indiceOrdem(adicional.uuid) === adicionais.length - 1}
                            onClick={() => trocarPosicaoAdicional(adicional.uuid, "baixo")}
                        >
                            <ArrowDownward fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </TableCell>
            <TableCell align="center">
                <img
                    alt={adicional.descricao}
                    src={
                        adicional.imagens?.length
                            ? (adicional.imagens[0] instanceof File
                                ? URL.createObjectURL(adicional.imagens[0] as File)
                                : adicional.imagens[0].url)
                            : `${process.env.PUBLIC_URL}/images/produtos/generico.png`
                    }
                    style={{ width: "80px", height: "70px", borderRadius: "5px" }}
                />
            </TableCell>
            <TableCell align="center">{adicional.descricao}</TableCell>
            <TableCell align="center">{adicional.detalhes || "-"}</TableCell>
            <TableCell align="center">{real(adicional.preco)}</TableCell>
            <TableCell align="center">
                <Chip
                    label={adicional.inativadoAs ? "Inativo" : "Ativo"}
                    color={adicional.inativadoAs ? "default" : "success"}
                    size="small"
                />
            </TableCell>
        </TableRow>
    );

    return (
        <Container>
            {tab === "tabela" && (
                <TableContainer variant="outlined" component={Paper} sx={{ width: "100%", maxHeight: 450, minHeight: 350 }}>
                    <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 }, display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="h6">Adicionais</Typography>
                        <FiltroContainer>
                            <Box>
                                <Button
                                    onClick={handleClick}
                                    endIcon={<ArrowDropDown />}
                                    variant="contained"
                                    disabled={adicionaisSelecionados.length <= 0}
                                >
                                    Ações
                                </Button>
                                <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                                    <MenuItem disabled>
                                        <Typography variant="inherit" noWrap>
                                            {adicionaisSelecionados.length} {adicionaisSelecionados.length === 1 ? "adicional selecionado" : "adicionais selecionados"}
                                        </Typography>
                                    </MenuItem>
                                    <Divider />
                                    <MenuItem disabled={adicionaisSelecionados.length !== 1} onClick={handleAbrirEdicao}>
                                        <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                                        <Typography variant="inherit" noWrap>Editar</Typography>
                                    </MenuItem>
                                    <MenuItem disabled={adicionaisSelecionados.length !== 1} onClick={handleToggleAtivacao}>
                                        <ListItemIcon><PowerSettingsNew fontSize="small" /></ListItemIcon>
                                        <Typography variant="inherit" noWrap>
                                            {adicionalSelecionado?.inativadoAs ? "Ativar" : "Inativar"}
                                        </Typography>
                                    </MenuItem>
                                    <MenuItem onClick={handleAbrirExclusao} disabled={adicionaisSelecionados.length === 0}>
                                        <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
                                        <Typography variant="inherit" noWrap>Excluir</Typography>
                                    </MenuItem>
                                </Menu>
                            </Box>
                            <MenuOrdenacao
                                titulo="Ordenar adicionais"
                                desabilitado={adicionais.length <= 1}
                                ordenar={(direcao) => {
                                    ordenarAdicionais(direcao);
                                    limparAdicionaisSelecionados();
                                }}
                            />
                            <Tooltip title="Cadastre um novo adicional">
                                <IconButton color="primary" onClick={() => handleTab("formulario")}>
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                        </FiltroContainer>
                    </Toolbar>
                    {adicionais.length ? (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center">
                                        <Checkbox
                                            checked={adicionaisSelecionados.length === adicionais.length && adicionais.length > 0}
                                            indeterminate={adicionaisSelecionados.length > 0 && adicionaisSelecionados.length < adicionais.length}
                                            onChange={toggleSelecionarTodos}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Define a ordem em que aparece no cardápio, no PDV e no chat. Salve o produto para gravar.">
                                            <span>Ordem</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">Imagem</TableCell>
                                    <TableCell align="center">Nome</TableCell>
                                    <TableCell align="center">Detalhes</TableCell>
                                    <TableCell align="center">Preço</TableCell>
                                    <TableCell align="center">Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {adicionais.map(renderizarLinha)}
                            </TableBody>
                        </Table>
                    ) : (
                        <TabelaVazia>Nenhum adicional cadastrado</TabelaVazia>
                    )}
                </TableContainer>
            )}

            {tab === "formulario" && (
                <Box sx={{ width: "100%" }}>
                    <Typography variant="h6">{adicionalEdicao ? "Editar adicional" : "Cadastrar adicional"}</Typography>
                    <form onSubmit={handleSubmit(adicionalEdicao ? handleEditar : handleCadastrar)}>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Nome"
                            type="text"
                            fullWidth
                            {...register("descricao")}
                            error={!!errors.descricao}
                            helperText={<>{errors.descricao?.message}</>}
                            value={descricao ?? ""}
                        />
                        <TextField
                            margin="dense"
                            label="Detalhes (opcional)"
                            type="text"
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={12}
                            {...register("detalhes")}
                            value={detalhes ?? ""}
                        />
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel error={!!errors.preco} htmlFor="preco-adicional">Preço</InputLabel>
                            <OutlinedInput
                                id="preco-adicional"
                                error={!!errors.preco}
                                startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                                label="Preço"
                                value={preco ?? ""}
                                {...register("preco")}
                            />
                            {!!errors.preco && <FormHelperText error>{errors.preco?.message as string}</FormHelperText>}
                        </FormControl>
                        <ImagemContainer>
                            <CardUploadImagem
                                titulo="Imagem"
                                descricao={
                                    imagem
                                        ? (imagem instanceof File ? imagem.name : (imagem?.nomeOriginal ?? imagem?.nome))
                                        : "Faça o upload"
                                }
                                url={imagem instanceof File ? URL.createObjectURL(imagem) : imagem?.url}
                                excluir={handleExcluirImagem}
                                upload={handleUploadImagem}
                            />
                        </ImagemContainer>
                        <ButtonsContainer>
                            <Button variant="outlined" type="button" onClick={() => handleTab("tabela")}>Cancelar</Button>
                            <Button variant="contained" type="submit">{adicionalEdicao ? "Atualizar" : "Cadastrar"}</Button>
                        </ButtonsContainer>
                    </form>
                </Box>
            )}

            {tab === "tabela" && (
                <ButtonsContainerForm>
                    <Button size="medium" variant="outlined" onClick={handleVoltar}>Voltar</Button>
                    <Button size="medium" variant="contained" onClick={handleProximo}>Próximo</Button>
                </ButtonsContainerForm>
            )}

            <ConfirmacaoAcaoModal
                open={confirmacaoExclusao}
                descricao={adicionaisSelecionados.length === 1 ? "Você deseja excluir o adicional selecionado?" : `Você deseja excluir os ${adicionaisSelecionados.length} adicionais selecionados?`}
                titulo={adicionaisSelecionados.length === 1 ? "Excluir adicional" : "Excluir adicionais"}
                onCancelar={handleFecharExclusao}
                onConfirmar={handleExcluirAdicionais}
            />
        </Container>
    );
}
